import { PUBLIC_EVENTS } from "@frontier/content/events";
import type { PublicEventsView } from "@frontier/content/events";
import { equipmentDefinition } from "@frontier/content/arsenal";
import type { Profile, WorldSnapshot } from "@frontier/protocol/contracts";
import { credits } from "./ui-labels";
import { pressHandlers } from "./press-handlers";
import { RelayReadout } from "./RelayReadout";
import { FACTIONS } from "@frontier/content/factions";
import { RELAY_PRESTIGE_TITLE } from "@frontier/content/relay";
import { eventContributionRules } from "@frontier/content/reward-rules";
import "./events.css";
interface Props {
  world: WorldSnapshot;
  view: PublicEventsView;
  profile: Pick<Profile, "tutorial" | "docked" | "lastEventResult" | "factionId">;
  connected: boolean;
  onJoin: (eventId: string) => void;
  onClose?: () => void;
  embedded?: boolean;
  navigationTracked?: boolean;
}
const statusLabels = {
  available: "ДОСТУПНО",
  active: "ИДЁТ СОБЫТИЕ",
  success: "УСПЕХ",
  failed: "ЗАВЕРШЕНО",
};
function remainingTime(seconds: number): string {
  const total = Math.max(0, Math.ceil(seconds));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}
const progressLabels = {
  escort: "Маршрут конвоя",
  raid: "Отбито волн",
  salvage: "Разобрано объектов",
  boss: "Прогресс уничтожения",
  relay: "Удержание фракции, с",
  truce: "Останови нарушителя",
};
export function PublicEventPanel({ world, view, profile, connected, onJoin, onClose, embedded = false, navigationTracked = true }: Props) {
  const allowed = profile.tutorial === null || profile.tutorial.docked;
  const result = profile.lastEventResult;
  return (
    <section
      className={`events-panel panel${embedded ? " events-embedded" : ""}`}
      role={embedded ? "region" : "dialog"}
      aria-label="Публичные события сектора"
      aria-modal={embedded ? undefined : "false"}
    >
      <div className="events-heading">
        <b>{embedded ? `СОБЫТИЯ · ${world.publicEvents.length}` : "СОБЫТИЯ ФРОНТИРА"}</b>
        {onClose && <button aria-label="Закрыть события" {...pressHandlers(onClose)}>
          ×
        </button>}
      </div>
      <div className="events-body">
      {!allowed && <p>Сначала помоги транспорту и вернись на станцию.</p>}
      {(view.factionBonuses ?? []).filter(bonus => bonus.expiresAt > Date.now()).map(bonus => <p className="event-faction-bonus" key={bonus.factionId}>
        <strong>{FACTIONS[bonus.factionId].name}: +{bonus.percent}% PvE-добычи</strong>
        <span>До {new Date(bonus.expiresAt).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}{bonus.factionId === profile.factionId ? " · твоя фракция" : ""}</span>
        {bonus.factionId === profile.factionId && <small>Бонус закрепляется при подборе. Задания и PvP-трофеи не умножаются; дробные доли накапливаются.</small>}
      </p>)}
      {view.trackedEventId && (
        <button
          className="event-contract-return"
          disabled={!connected}
          {...pressHandlers(() => {
            onJoin("");
            onClose?.();
          })}
        >
          ← Вернуться к навигации контракта
        </button>
      )}
      <div className="events-list">
        {world.publicEvents.map((event) => {
          const own = view.participation.find((item) => item.eventId === event.id);
          const selected = event.id === view.trackedEventId;
          const navigating = selected && navigationTracked;
          const active = event.status === "available" || event.status === "active";
          const definition = event.terms;
          const contributionRules = event.type === "relay" || event.type === "truce" ? null : eventContributionRules(definition);
          const contributionRequirement = event.type === "truce"
            ? "Нанеси урон нарушителю или отремонтируй защитника"
            : contributionRules === null
            ? "Удерживай точку своей фракцией по условиям события"
            : `Нужен вклад: урон ≥ ${contributionRules.damage}, ремонт ≥ ${contributionRules.healing} или задачи ≥ ${contributionRules.tasks}`;
          const factionRequired = event.type === "relay" && !profile.factionId;
          return (
            <article
              key={event.id}
              className={`public-event-card ${navigating ? "tracked" : ""}`}
              data-event-id={event.id}
            >
              {embedded && <div className="journal-art" aria-hidden="true"><img src={event.type === "escort" ? "/assets/transport.png" : event.type === "salvage" ? "/assets/container.png" : event.type === "relay" || event.type === "truce" ? "/assets/station.png" : event.type === "boss" ? "/assets/combat/boss.png" : "/assets/combat/fighter.png"} alt="" /></div>}
              <div className="event-card-heading">
                <h2>{event.title}</h2>
                <span>{statusLabels[event.status]}</span>
              </div>
              <p className="event-description">{definition.description}</p>
              {!embedded && <p className="event-condition">{definition.condition}</p>}
              <RelayReadout event={event} presence={own?.tasks} />
              {factionRequired && <p className="relay-risk">Для захвата выбери фракцию.</p>}
              {event.workBlocked && (
                <strong className="event-work-blocked">ОХРАНА МЕШАЕТ РАЗБОРУ</strong>
              )}
              {event.type !== "truce" && <div className="event-progress">
                <i
                  style={{
                    width: `${Math.max(0, Math.min(100, (100 * event.progress) / event.goal))}%`,
                  }}
                />
              </div>}
              <div className="event-facts">
                <span>
                  {event.type === "truce" ? progressLabels.truce : `${progressLabels[event.type]}: ${Math.round(event.progress)}/${event.goal}`}
                </span>
                <span>
                  {event.type === "relay" ? `${event.participants} поблизости` : event.type === "truce" ? `Защитники: ${event.participants}` : `${event.participants} участвуют · силы для ${event.scaledFor}`}
                </span>
                <span>
                  {event.hasDeadline === false ? (active ? "До выхода нарушителя" : "Охота завершена") : event.status === "available"
                    ? event.type === "relay" ? `До сигнала ${remainingTime(event.remaining)}` : "ПОДЛЕТИ К МАЯКУ"
                    : `Осталось ${remainingTime(event.remaining)}`}
                </span>
              </div>
              {own && (
                <div className="event-contribution">
                  {event.type === "relay" ? "ЛИЧНЫЙ ДОПУСК К НАГРАДЕ" : event.type === "truce" ? "ПОМОЩЬ ОХРАНЕ" : <>ТВОЙ ВКЛАД · урон {Math.floor(own.damage)} · ремонт {Math.floor(own.healing)} · задачи {own.tasks}</>}
                  <b>
                    {own.pending
                      ? "Начисляем награду…"
                      : event.type === "relay" && (event.status === "success" || event.status === "failed")
                        ? own.eligible ? "Захват засчитан" : "Награда за этот захват не получена"
                      : own.eligible
                        ? event.type === "relay" ? "Допуск получен · оставайся на точке до победы" : "Участие засчитано"
                        : contributionRequirement}
                  </b>
                </div>
              )}
              {embedded && !own && <div className="event-contribution">Твой вклад: пока нет</div>}
              {embedded && <details className="event-details"><summary>Подробнее</summary><p className="event-condition">{definition.condition}</p></details>}
              <div className="event-card-bottom">
                <div className="journal-reward">
                  <small>{event.type === "relay" ? "За победу и присутствие" : "За успех и участие"}</small>
                  <div><span className="journal-amount">+{credits(definition.credits)} КР</span> <span className="journal-amount">+{definition.components} комп.</span></div>
                </div>
                <button
                  disabled={!connected || !allowed || factionRequired || !active || (navigating && !profile.docked)}
                  {...pressHandlers(() => {
                    onJoin(event.id);
                    onClose?.();
                  })}
                >
                  {navigating && !profile.docked
                    ? "ОТСЛЕЖИВАЕТСЯ"
                    : profile.docked
                      ? "ОТСТЫКОВАТЬСЯ"
                      : selected
                        ? "ПОКАЗАТЬ МАРШРУТ"
                      : "ПРИСОЕДИНИТЬСЯ"}
                </button>
              </div>
            </article>
          );
        })}
      </div>
      {result && (
        <section
          className="event-receipt"
          aria-label="Подтверждённый результат события"
          data-receipt-event-id={result.eventId}
        >
          <span className="eyebrow">
            ПОДТВЕРЖДЁННЫЙ РЕЗУЛЬТАТ ·{" "}
            {result.terms?.title ?? (result.eventType === "truce" ? "Сигнал охраны" : PUBLIC_EVENTS[result.eventType].title)}
          </span>
          <b>
            {result.outcome === "success"
              ? result.eligible
                ? `Получено: +${credits(result.credits)} КР · +${result.components} компонентов`
                : "Событие выполнено. Личного вклада недостаточно для награды."
              : "Событие завершилось неудачей."}
          </b>
          <small>
            Урон {Math.floor(result.contribution.damage)} · ремонт{" "}
            {Math.floor(result.contribution.healing)} · задачи {result.contribution.tasks}
          </small>
          {result.blueprintUnlocks.length > 0 && (
            <small>
              Открыты чертежи:{" "}
              {result.blueprintUnlocks.map((key) => equipmentDefinition(key).name).join(" · ")}
            </small>
          )}
          {result.eventType === "relay" && result.winnerFaction && (
            <small>Победила фракция: {FACTIONS[result.winnerFaction].name}.
              {result.eligible && result.outcome === "success" && ` Засчитан захват · звание «${RELAY_PRESTIGE_TITLE}».`}
            </small>
          )}
        </section>
      )}
      </div>
    </section>
  );
}
